"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { BookOpen, ArrowRight } from "lucide-react";

interface GuideTopic {
  title: string;
  description: string;
  slug: string;
  readTime: string;
  category: string;
}

interface GuidesListProps {
  topics: GuideTopic[];
}

export function GuidesList({ topics }: GuidesListProps) {
  const searchParams = useSearchParams();
  const urlTopic = searchParams?.get("topic");
  const [activeTopic, setActiveTopic] = useState<string | null>(
    urlTopic ? decodeURIComponent(urlTopic) : null
  );

  const visibleTopics = useMemo(
    () => (activeTopic ? topics.filter((t) => t.category === activeTopic) : topics),
    [topics, activeTopic]
  );

  const handleCategoryChange = (category: string | null) => {
    setActiveTopic(category);
    if (category) {
      window.history.pushState({}, "", `/guides?topic=${encodeURIComponent(category)}`);
    } else {
      window.history.pushState({}, "", `/guides`);
    }
  };

  return (
    <>
      {activeTopic && (
        <div className="mt-4">
          <button
            onClick={() => handleCategoryChange(null)}
            className="inline-flex items-center gap-1 text-sm font-medium text-[var(--color-accent-hover)] hover:text-[var(--color-accent-hover)]"
          >
            ← Show all guides
          </button>
        </div>
      )}

      <div className="grid gap-4">
        {visibleTopics.map((topic) => (
          <Link
            key={topic.slug}
            href={`/guides/${topic.slug}`}
            className="group flex items-start gap-4 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-5 transition-all hover:-translate-y-0.5"
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[var(--color-accent-soft)] text-[var(--color-accent-hover)]">
              <BookOpen className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-[var(--color-text)] group-hover:text-[var(--color-accent)]">
                {topic.title}
              </h3>
              <p className="mt-1 text-sm text-[var(--color-text-muted)] line-clamp-2">
                {topic.description}
              </p>
              <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                {topic.category} · {topic.readTime} read
              </p>
            </div>
            <ArrowRight className="mt-2 h-4 w-4 flex-shrink-0 text-[var(--color-text-muted)]" />
          </Link>
        ))}
      </div>

      {visibleTopics.length === 0 && (
        <div             className="mt-8 rounded-md border border-dashed border-[var(--color-border-strong)] p-8 text-center text-sm text-[var(--color-text-muted)]">
          No guides found for this topic.
        </div>
      )}
    </>
  );
}